import Controller, { OnBeforeSubmit, OnCancel } from '@philips-internal/oswald-ui-core/lib/client';
import { FormAPI, ModalService, TextAreaAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';

import { Inject } from '@odinjs/odin';

import { FragmentNames, HealthHistoryInactivateAliases } from '../../../codegen';
import BaseController from '../../BaseController';
import { DialogType, ModalType, SimpleDialogParams } from '../../../interfaces/simple-dialog';
import { SimpleDialog } from '../../dialog-helper';
import { HealthHistoryListContext } from '../service/health-history-list-service';
import { HealthHistoryType } from '../health-history-types';
import { Endpoint } from '../../../constants/endpoints';

@Controller({ fragment: FragmentNames.HEALTH_HISTORY_INACTIVATE })
export default class HealthHistoryInactivateController extends BaseController {

  @Inject(HealthHistoryInactivateAliases.FORM_HEALTH_HISTORY_INACTIVATE)
    formHealthHistoryInactivateAPI!: FormAPI;

  @Inject(HealthHistoryInactivateAliases.TEXT_AREA_JUSTIFY)
    textAreaJustificationApi!: TextAreaAPI;

  private inactivateHealthHistory(endpoint: string): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    const justification = this.textAreaJustificationApi.getValue();

    this.formHealthHistoryInactivateAPI.setLoading(true);

    const body = {
      idHealthHistory,
      justification
    } as Record<string, Value>;

    HttpService.put(endpoint, undefined, { body })
      .then(() => {
        this.historyService.back();
        this.showMessageDialog('title.disable-record', 'patient-evolution.successfully-inactivated-message')
      }).catch(err => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => {
        this.formHealthHistoryInactivateAPI.setLoading(false);
      });

  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-inactivate',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private showMessageDialog(title: string, description: string): void {
    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: description,
      dialogType: DialogType.ALERT,
      modalType: ModalType.DIALOG,
      onClickOk: (): void => ModalService.close(FragmentNames.HEALTH_HISTORY_INACTIVATE),
      titleDialog: title,
      titleOnOk: 'OK',
    }
    SimpleDialog.openDialog(dialogParams);
  }

  @OnCancel({ target: HealthHistoryInactivateAliases.FORM_HEALTH_HISTORY_INACTIVATE })
  handleCancelForm(): void {
    ModalService.close(FragmentNames.HEALTH_HISTORY_INACTIVATE);
  }

  @OnBeforeSubmit({ target: HealthHistoryInactivateAliases.FORM_HEALTH_HISTORY_INACTIVATE })
  handleBeforeSubmit(): void {
    const tab = HealthHistoryListContext.currentTab;
    switch (tab) {
      case HealthHistoryType.ALLERGIES:
        return this.inactivateHealthHistory(Endpoint.INACTIVATE_HEALTH_HISTORY_ALLERGIES);
      case HealthHistoryType.DISEASES:
        return this.inactivateHealthHistory(Endpoint.INACTIVATE_HEALTH_HISTORY_DISEASES);
      case HealthHistoryType.MEDICINES:
        return this.inactivateHealthHistory(Endpoint.INACTIVATE_HEALTH_HISTORY_MEDICINES);
      case HealthHistoryType.SOCIAL_HISTORY:
        return this.inactivateHealthHistory(Endpoint.INACTIVATE_HEALTH_HISTORY_SOCIAL_HISTORY);
      default:
        return this.inactivateHealthHistory(Endpoint.INACTIVATE_HEALTH_HISTORY_ALLERGIES);
    }
  }
}
